import {Controller} from "@hotwired/stimulus"
import {useZoomListener} from "./mixins/use_zoom_listener";
import {useResizeListener} from "./mixins/use_resize_listener";
import {useLeftPanelChangeListener} from "./mixins/use_left_panel_change_listener" ;

/**
 * A Stimulus controller that manages the collapsing state
 * of an element based on horizontal visibility within the viewport.
 *
 * If the flex element does not fit within the viewport,
 * the controller adds a 'collapsed-self' class to it.
 *
 * Collapsed state sets 'flex-wrap: wrap' on the flex container.
 *
 * Usage:
 *  .collapsed-self:flex-wrap{data: {controller: 'flex-wrap'}}
 *
 * It responds to resize and zoom changes by updating
 * CSS classes to reflect visibility.
 *
 * Requires the useZoomListener and useResizeListener mixins.
 */

export default class extends Controller {
  connect() {
    super.connect();
    useZoomListener(this);
    useResizeListener(this);
    useLeftPanelChangeListener(this);
    this._setElementState();
  }

  disconnect() {
    super.disconnect();
  }

  onResizeChange() {
    this._setElementState();
  }

  onZoomChange() {
    this._setElementState();
  }

  onLeftPanelChange() {
    this._setElementState();
  }

  _setElementState() {
    this.element.classList.remove('collapsed-self');
    this.element.classList.add('invisible');

    const isHorizontallyFullyVisible = this._isHorizontallyFullyVisible();
    if (!isHorizontallyFullyVisible) {
      this.element.classList.add('collapsed-self');
    }

    this.element.classList.remove('invisible');
  }

  _isHorizontallyFullyVisible() {
    const elementRectangle = this.element.getBoundingClientRect();
    const windowWidth = window.innerWidth;

    return elementRectangle.width > 0 &&
      elementRectangle.left + 1 >= 0 &&
      elementRectangle.right - 1 <= windowWidth;
  }
}